import { Link } from "react-router-dom";
import { Ticket, MessagesSquare, Tag, ArrowRight, MessageCircle, BookOpenCheck } from "lucide-react";
import { BRAND } from "@/lib/api";
import { Logo } from "@/components/Logo";

export const HomeTopNav = ({ user }) => (
  <header className="relative z-10" data-testid="home-topbar">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
      <Logo size="h-12 sm:h-16" tagline={false} siteLink testId="home-logo-link" />
      <nav className="flex items-center gap-2 sm:gap-6 text-sm">
        <Link to="/community" data-testid="nav-community-link" className="hidden text-slate-300 hover:text-white sm:inline">Community</Link>
        <Link to="/fixes" data-testid="nav-fixes-link" className="hidden text-slate-300 hover:text-white sm:inline">Known fixes</Link>
        <Link to="/for-sale" data-testid="nav-forsale-link" className="hidden text-slate-300 hover:text-white sm:inline">For sale</Link>
        {user ? (
          <Link to="/dashboard" data-testid="nav-dashboard-link" className="btn-primary">Dashboard</Link>
        ) : (
          <Link to="/auth" data-testid="nav-signin-link" className="btn-primary">Sign in</Link>
        )}
      </nav>
    </div>
    <div className="flex h-9 items-center justify-center border-y border-slate-800/70 bg-slate-950/40" data-testid="home-topbar-title-row">
      <Link to="/" data-testid="home-topbar-title" className="whitespace-nowrap font-heading text-sm font-bold uppercase tracking-[0.3em] text-white sm:text-base">
        Support <span className="text-rose-500">Centre</span>
      </Link>
    </div>
  </header>
);

export const HeroCopy = () => (
  <div className="relative z-10 mx-auto max-w-7xl px-6 pb-24 pt-16 lg:px-10 lg:pb-32 lg:pt-24">
    <h1 className="fade-up delay-1 max-w-3xl font-heading text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
      Precision support for the machines that keep you <span className="brand-gradient-text">cutting.</span>
    </h1>
    <div className="laser-line fade-up delay-2 mt-8 w-48" />
    <p className="fade-up delay-2 mt-8 max-w-xl text-base text-slate-300 sm:text-lg">
      Raise tickets with our engineers, learn from other operators, and buy or sell machines and parts — all in one place.
    </p>
    <div className="fade-up delay-3 mt-10 flex flex-wrap gap-3">
      <Link to="/tickets/new" data-testid="hero-new-ticket-btn" className="btn-primary px-5 py-3">Submit a ticket <ArrowRight className="h-4 w-4" /></Link>
      <Link to="/for-sale" data-testid="hero-marketplace-btn" className="btn-ghost px-5 py-3">Browse for sale</Link>
      <a href={BRAND.whatsappLink} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="btn-ghost px-5 py-3">
        <MessageCircle className="h-4 w-4 text-emerald-400" /> WhatsApp us
      </a>
    </div>
  </div>
);

const CARDS = [
  { to: "/tickets/new", icon: Ticket, title: "Submit a ticket", body: "Free, or £30 for a priority response within 4 working hours. FLS engineers answer every ticket.", id: "home-card-ticket" },
  { to: "/community", icon: MessagesSquare, title: "Ask the community", body: "Search solved threads on CO2 lasers, optics, chillers and software.", id: "home-card-community" },
  { to: "/fixes", icon: BookOpenCheck, title: "Known fixes", body: "Published, FLS-approved fixes for common faults. Search by error code or symptom.", id: "home-card-fixes" },
  { to: "/for-sale", icon: Tag, title: "Machines & parts for sale", body: "Browse used machines and spares listed by other operators.", id: "home-card-forsale" },
];

export const HomeCards = () => (
  <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
    {CARDS.map(({ to, icon: Icon, title, body, id }, i) => (
      <Link key={to} to={to} data-testid={id} className={`panel panel-hover fade-up group p-7 delay-${i + 1}`}>
        <Icon className="h-6 w-6 text-rose-400" />
        <h3 className="mt-6 font-heading text-xl font-semibold text-white">{title}</h3>
        <p className="mt-2 text-sm text-slate-400">{body}</p>
        <span className="mt-6 inline-flex items-center gap-1 text-sm text-sky-400 group-hover:gap-2 transition-[gap]">Open <ArrowRight className="h-4 w-4" /></span>
      </Link>
    ))}
  </div>
);

export const StatsBar = ({ stats }) => (
  <div className="mb-12 flex flex-wrap gap-10 font-mono text-xs uppercase tracking-widest text-slate-500">
    <span data-testid="stat-threads"><b className="mr-2 font-heading text-3xl text-white">{stats?.threads ?? "–"}</b>community threads</span>
    <span data-testid="stat-listings"><b className="mr-2 font-heading text-3xl text-white">{stats?.listings ?? "–"}</b>items for sale</span>
    <span><b className="mr-2 font-heading text-3xl text-white">UK</b>based engineers</span>
  </div>
);

const SERVICES = ["Collection & relocation", "Installation & commissioning", "Operator training", "Servicing, diagnostics & repairs"];

export const ServicesBand = () => (
  <section className="border-y border-slate-800 bg-slate-900/40" data-testid="home-services-band">
    <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 py-16 lg:grid-cols-[1fr_1.1fr] lg:px-10 lg:py-20">
      <div className="fade-up">
        <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-rose-400">FLS on the road</p>
        <h2 className="font-heading text-3xl font-bold leading-tight text-white sm:text-4xl">Bought a machine? We'll collect it, install it and train your team.</h2>
        <p className="mt-4 max-w-lg text-sm text-slate-400 sm:text-base">
          Our engineers cover the UK for machine collection and relocation, installation and commissioning, servicing, diagnostics and operator training — including machines bought privately through the marketplace.
        </p>
        <ul className="mt-6 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
          {SERVICES.map((s) => <li key={s} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> {s}</li>)}
        </ul>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/for-sale" data-testid="services-marketplace-btn" className="btn-primary px-5 py-3">Browse machines & get a quote <ArrowRight className="h-4 w-4" /></Link>
          <a href={BRAND.whatsappLink} target="_blank" rel="noreferrer" data-testid="services-whatsapp-btn" className="btn-ghost px-5 py-3"><MessageCircle className="h-4 w-4 text-emerald-400" /> Ask on WhatsApp</a>
        </div>
      </div>
      <div className="fade-up delay-2 relative">
        <div className="absolute -inset-3 rounded-xl bg-gradient-to-br from-[#2F6BEB]/20 via-[#7B3FA0]/20 to-[#C0141B]/20 blur-2xl" />
        <img src="/brand/fls-van.webp" alt="Focused Laser Systems service van on the motorway" className="relative w-full rounded-lg border border-slate-800 object-cover shadow-2xl" data-testid="home-van-image" />
      </div>
    </div>
  </section>
);
